import {RenderingPrioritizerBoundary} from '@aboutyou/data-fetcher';
import {useIsFeatureActive} from '@aboutyou/feature-flags';
import {Product} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/product.types';
import {
  ANCHOR_OFFSET_FROM_STICKY_HEADER,
  NavigationTreeLoading,
  ProductMiniAdpWithBasketFlyout,
  STREAM_TOP_SPACING,
  SizeSelectionOpenedStateKey,
  WishlistHeader,
  WishlistHeaderBanner,
  WishlistItemStream,
  WishlistItemStreamLoading,
  WishlistNavigationTree,
  WishlistPageMeta,
  WishlistSizeReminder,
  createSimilarProductsPageUrl,
  useAutoAddToWishlist,
  useMiniAdpDialogOpenedState,
  useSimilarProductsPageOriginClick,
  useSizeReminderPopupOpenedState,
} from '@aboutyou/logic-components';
import {ScrollTopAnchor, useHistory} from '@aboutyou/router';
import {useTrackFeature} from '@aboutyou/tracking';
import {
  RowSection,
  SplitType,
  SplitView,
  Z_INDEXES,
} from '@aboutyou/ui-components';
import {useClearWishlistBubble, useWishlistCount} from '@aboutyou/user';
import React, {useCallback, useEffect, useRef} from 'react';
import {Sticky} from 'react-stickup-ay';
import styled from 'styled-components';

const splitViewConfig: {
  split: SplitType;
  splitMedium: SplitType;
  splitLarge: SplitType;
} = {
  split: '30:70',
  splitMedium: '20:80',
  splitLarge: '15:85',
};

export const WishlistPageEager = () => {
  const sidebarRef = useRef<HTMLDivElement>(null);
  const topanchorRef = useRef<HTMLDivElement>(null);
  const history = useHistory();
  const clearWishlistBubbleCallback = useClearWishlistBubble();

  const trackFeature = useTrackFeature();
  const trackSimilarPageOriginClick = useSimilarProductsPageOriginClick();
  const {open: openMiniAdp} = useMiniAdpDialogOpenedState();

  const {open: openSizeReminder, parameter: sizeReminderParam} =
    useSizeReminderPopupOpenedState();

  useEffect(() => {
    clearWishlistBubbleCallback();
  }, [clearWishlistBubbleCallback]);

  const handleOnClickAddToBasket = useCallback(
    ({
      productId,
      couponPromotionId,
    }: {
      productId: number;
      couponPromotionId?: string;
    }) => {
      trackFeature({eventType: 'click', label: 'WL_A2B'});
      openMiniAdp({productId, couponPromotionId, locationId: 'product_stream'});
    },
    [],
  );

  const handleOnClickSoldoutCta = useCallback((productId: number) => {
    openSizeReminder({parameter: `${productId}`});
  }, []);

  const onSimilarProductsClick = useCallback((product: Product) => {
    trackFeature({eventType: 'click', label: 'WL_similar_products'});
    trackSimilarPageOriginClick();
    history.push(createSimilarProductsPageUrl(product));
  }, []);

  const {count: wishlistCount} = useWishlistCount();
  const isWishlistEmpty = wishlistCount === 0;

  const {isActive: interactiveHoverMode} = useIsFeatureActive(
    'desk_interactive_tile_wishlist',
    {
      abTest: true,
    },
  );

  const {isAutoAddToWishlistLoading} = useAutoAddToWishlist();

  if (isAutoAddToWishlistLoading) {
    return (
      <RowSection>
        <StyledSection>
          <WishlistPageMeta />
          <SplitView
            {...splitViewConfig}
            contentStart={!isWishlistEmpty && <StyledNavigationTreeLoading />}
            contentEnd={
              <StyledSection data-increase-padding={interactiveHoverMode}>
                <WishlistItemStreamLoading />
              </StyledSection>
            }
          />
        </StyledSection>
      </RowSection>
    );
  }

  return (
    <RowSection>
      <WishlistPageMeta />
      <ScrollTopAnchor
        defaultOffsetTop={ANCHOR_OFFSET_FROM_STICKY_HEADER}
        anchorRef={topanchorRef}
        as={SplitView}
        {...splitViewConfig}
        disableSplit={isWishlistEmpty}
        refStart={sidebarRef}
        contentStart={
          !isWishlistEmpty && (
            <Sticky
              overflowScroll="flow"
              experimentalNative
              container={sidebarRef}
              style={{zIndex: Z_INDEXES.CATEGORY_TREE}}
              defaultOffsetTop={ANCHOR_OFFSET_FROM_STICKY_HEADER}
            >
              <StyledWishlistNavigationTree excludeUrlParams={['sort']} />
            </Sticky>
          )
        }
        contentEnd={
          <StyledSection data-increase-padding={interactiveHoverMode}>
            <RenderingPrioritizerBoundary>
              <WishlistHeaderBanner renderingPriority={1} />
              {!isWishlistEmpty && <WishlistHeader filterVariant="dropdown" />}
              <WishlistItemStream
                onClickAddToBasket={handleOnClickAddToBasket}
                onClickSoldoutCta={handleOnClickSoldoutCta}
                onSimilarProductsClick={onSimilarProductsClick}
                interactiveHoverMode={interactiveHoverMode}
                productTileGalleryMode={
                  interactiveHoverMode ? 'hover' : undefined
                }
                renderingPriority={2}
              />
            </RenderingPrioritizerBoundary>

            <ProductMiniAdpWithBasketFlyout
              sizeSelectionStateManagerKey={
                SizeSelectionOpenedStateKey.MAIN_SIZE_SELECTION
              }
            />
          </StyledSection>
        }
      />
      <WishlistSizeReminder productId={Number(sizeReminderParam ?? '')} />
    </RowSection>
  );
};

const StyledSection = styled.section`
  padding-top: ${STREAM_TOP_SPACING}px;

  &[data-increase-padding='true'] {
    padding-bottom: 1rem;
  }
`;

const StyledWishlistNavigationTree = styled(WishlistNavigationTree)`
  padding-top: ${STREAM_TOP_SPACING}px;
  word-break: break-word;
`;

const StyledNavigationTreeLoading = styled(NavigationTreeLoading)`
  padding-top: ${STREAM_TOP_SPACING}px;
  word-break: break-word;
`;
